/**
 * React Query hooks for Orders
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ordersService } from "../services/orders.service";
import type { CreateOrderRequest, FilterParams } from "../types";
import { toast } from "sonner";

export const useOrders = (params?: FilterParams) => {
  return useQuery({
    queryKey: ["orders", params],
    queryFn: () => ordersService.getOrders(params),
  });
};

export const useOrder = (orderId: string) => {
  return useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => ordersService.getOrderById(orderId),
    enabled: !!orderId,
  });
};

export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateOrderRequest) => ordersService.createOrder(data),
    onSuccess: (data) => {
      toast.success(data.message || "Order created successfully");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create order");
    },
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: Partial<any> }) =>
      ordersService.updateOrder(orderId, data),
    onSuccess: () => {
      toast.success("Order updated successfully");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update order");
    },
  });
};

export const useDeleteOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (orderId: string) => ordersService.deleteOrder(orderId),
    onSuccess: () => {
      toast.success("Order deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete order");
    },
  });
};

export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      orderId,
      status,
    }: {
      orderId: string;
      status: "pending" | "confirmed" | "delivered" | "cancelled";
    }) => ordersService.updateOrderStatus(orderId, status),
    onSuccess: () => {
      toast.success("Order status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update order status");
    },
  });
};

export const useOrderDetails = (params: {
  firmId: number;
  orderNo: number;
  partyCode?: string;
  salesManCode?: string;
}, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["orderDetails", params],
    queryFn: () => ordersService.getOrderDetails(params),
    enabled: enabled && !!params.orderNo && !!params.firmId,
  });
};

